/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import EnvironmentIndicator from '@ion/applications/src/env/EnvironmentIndicator';
import useEnvironmentDetails from '@ion/applications/src/env/useEnvironmentDetails';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import DefaultAvatarIcon from '@ion/dls/src/icons/DefaultAvatarIcon';
import TopNavHelpIcon from '@ion/dls/src/icons/TopNavHelpIcon';
import TopNavNotificationIcon from '@ion/dls/src/icons/TopNavNotificationIcon';
import Render from '@ion/dls/src/render/Render';
import NewIonThemeSwitch from '@ion/dls/src/theme/NewIonThemeSwitch';
import {dlsColor} from '@ion/dls/src/theme/theme';
import ThemeModeToggle, {ThemeModeToggleClasses} from '@ion/dls/src/theme/ThemeModeToggle';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {MenuItem} from '@ion/dls/src/types/components';
import {IconSize} from '@ion/dls/src/types/types';
import {APP_HELP, APP_NOTIFICATION} from '@/security/Features';
import {canToggleThemeMode, hasNewThemeToggleAccess} from '@/security/themeAccess';
import {setActiveIndex, toggleSecondaryNav} from '@/store/actions/nav';
import {viewHelp} from '../../header/DesktopUserProfileView';
import NavigationHeaderContent from './NavigationHeaderContent';
import NavigationHeaderUtilities from './NavigationHeaderUtilities';

/**
 * Defines the tooltip class overrides consumed by current-navigation tooltips.
 */
interface TooltipOverrideClasses {
  popper: string;
  tooltip: string;
}

/**
 * Defines the inputs required to manage current-theme-only suite side navigation behavior.
 */
interface UseCurrentNavigationParams {
  Features?: ApplicationFeatures;
  activeIndex: number;
  activeSubmenuIndex: number;
  currentPage: string;
  dispatch: Function;
  globalGroupTitle?: string;
  isCurrent: boolean;
  isDesktop: boolean;
  isReseller: boolean;
  isViewingGlobalGroup?: boolean;
  multiThemeEnabled: boolean;
  onBrandClick: () => void;
  onOpenProfile: () => void;
  onToggleNotifications: () => void;
  openGroupChooser: () => void;
  openTracking: () => void;
  openTrackingFilter: () => void;
  openTrackingSearch: () => void;
  secondaryNav?: {
    show: boolean;
  };
  tooltipClasses: {
    popper: string;
    tooltip: string;
  };
  trackingFilterBadgeCount?: number;
  trackingFilterSelected?: boolean;
  trackingSelected?: boolean;
  unreadCount?: number;
}

// defines the layout applied to each footer item rendered below the footer menu ...
const footerItemStyle = {
  paddingTop    : 8,
  paddingLeft   : 16,
  paddingBottom : 8
};

// defines the layout applied to a footer item while the navigation rail is collapsed,
// which is too narrow for the expanded item's left padding ...
const collapsedFooterItemStyle = {
  paddingTop     : 8,
  paddingBottom  : 8,
  display        : 'flex',
  justifyContent : 'center'
};

// defines the dark mode toggle, rendered in the row of the Profile footer
// item with the class names the footer applies to its own icons ...
const renderThemeModeToggle = (classes: ThemeModeToggleClasses) => (
  <ThemeModeToggle canToggle={canToggleThemeMode()}
    slot='nav'
    classes={classes} />
);

/**
 * Manages current-theme-specific side nav state, effects, and navigation chrome props.
 *
 * @param params Defines the current side navigation state and external callbacks.
 * @returns Current-theme navigation props and behavior helpers.
 */
export const useCurrentNavigation = ({
  Features,
  activeIndex,
  activeSubmenuIndex,
  currentPage,
  dispatch,
  globalGroupTitle,
  isCurrent,
  isDesktop,
  isReseller,
  multiThemeEnabled,
  onBrandClick,
  onOpenProfile,
  onToggleNotifications,
  openGroupChooser,
  openTracking,
  openTrackingFilter,
  openTrackingSearch,
  secondaryNav,
  tooltipClasses,
  trackingFilterBadgeCount = 0,
  trackingFilterSelected = false,
  trackingSelected = false,
  unreadCount,
  isViewingGlobalGroup = false
}: UseCurrentNavigationParams) => {
  const skipNextRouteSyncedSubmenuRef                   = useRef(false);
  const hasInitializedRouteSyncRef                      = useRef(false);
  const previousRouteRef                                = useRef(currentPage);
  const previousGroupRef                                = useRef(globalGroupTitle);
  const previousSubmenuIndexRef                         = useRef(activeSubmenuIndex);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const {isExternal: isExternalEnvironment}             = useEnvironmentDetails();
  const {mode}                                          = useThemeMode();

  const markSkipNextRouteSync = useCallback(() => {
    skipNextRouteSyncedSubmenuRef.current = true;
  }, []);

  const closeSecondaryNavigation = useCallback(() => {
    skipNextRouteSyncedSubmenuRef.current = true;
    dispatch(toggleSecondaryNav(false));
    dispatch(setActiveIndex(-1));
  }, [dispatch]);

  useEffect(() => {
    if (!isCurrent) {
      return;
    }
    const isInitialSync   = !hasInitializedRouteSyncRef.current;
    const hasRouteChanged = previousRouteRef.current !== currentPage;
    // a switch of group re-builds the menu - a restricted group's few pages
    // giving way to every page, say - so the open submenu's index can point
    // at a different item (Dashboard) though the page stays the same. The
    // open submenu is synced to the page again whenever the group, or the
    // current page's own place in the menu, changes ...
    const hasMenuChanged  = previousGroupRef.current !== globalGroupTitle ||
      previousSubmenuIndexRef.current !== activeSubmenuIndex;
    if (!isInitialSync && !hasRouteChanged && !hasMenuChanged) {
      return;
    }
    hasInitializedRouteSyncRef.current = true;
    previousRouteRef.current = currentPage;
    previousGroupRef.current = globalGroupTitle;
    previousSubmenuIndexRef.current = activeSubmenuIndex;
    if (skipNextRouteSyncedSubmenuRef.current) {
      skipNextRouteSyncedSubmenuRef.current = false;
      return;
    }
    if (activeSubmenuIndex >= 0) {
      if (activeIndex !== activeSubmenuIndex) {
        dispatch(setActiveIndex(activeSubmenuIndex));
      }
      dispatch(toggleSecondaryNav(true));
      return;
    }
    if (secondaryNav?.show) {
      dispatch(toggleSecondaryNav(false));
    }
    if (activeIndex !== -1) {
      dispatch(setActiveIndex(-1));
    }
  }, [activeIndex, activeSubmenuIndex, currentPage, dispatch, globalGroupTitle, isCurrent, secondaryNav]);

  const toggleContactSupport = useCallback((isOpen: boolean) => {
    setIsContactSupportOpen(isOpen);
  }, []);

  const openContactSupport = useCallback(() => {
    toggleContactSupport(true);
  }, [toggleContactSupport]);

  const closeContactSupport = useCallback(() => {
    toggleContactSupport(false);
  }, [toggleContactSupport]);

  const getMobileHeaderAction = useCallback((action: () => void, onClose?: () => void) => () => {
    onClose?.();
    setTimeout(action, 0);
  }, []);

  const footerItems = useMemo(() => {
    const items: MenuItem[] = [];
  
    if (Features?.hasFeature(APP_NOTIFICATION)) {
      items.push({
        icon         : <TopNavNotificationIcon iconColor={dlsColor('grey1')} iconSize={IconSize.medium} />,
        id           : 'alerts',
        isActive     : true,
        isAuthorized : true,
        isPreview    : true,
        key          : 'alerts-nav-footer',
        label        : translate('nav:navigation.alerts'),
        meta         : {
          badgeCount : unreadCount || undefined,
          onClick    : onToggleNotifications
        }
      });
    }
    if (Features?.hasFeature(APP_HELP)) {
      items.push({
        icon         : <TopNavHelpIcon iconColor={dlsColor('grey1')} iconSize={IconSize.medium} />,
        id           : 'help',
        isActive     : true,
        isAuthorized : true,
        isPreview    : true,
        key          : 'help-nav-footer',
        label        : translate('nav:ionprime.help'),
        meta         : {
          onClick : viewHelp()
        }
      });
    }

    // the Profile item shares its row with the dark mode toggle only where the
    // toggle renders, as it is narrowed to make room for it ...
    const hasThemeModeToggle = isCurrent && multiThemeEnabled && canToggleThemeMode();

    items.push({
      icon         : <DefaultAvatarIcon iconColor={dlsColor('grey1')} iconSize={IconSize.medium} />,
      id           : 'profile',
      isActive     : true,
      isAuthorized : true,
      isPreview    : true,
      key          : 'profile-nav-footer',
      label        : translate('nav:navigation.profile'),
      meta         : {
        onClick        : onOpenProfile,
        renderTrailing : hasThemeModeToggle ? renderThemeModeToggle : undefined
      }
    });

    return items;
  }, [
    isCurrent,
    multiThemeEnabled,
    mode,
    unreadCount,
    onOpenProfile,
    onToggleNotifications
  ]);

  // the header utilities' props shared by the desktop rail and the mobile
  // sidesheet, so both apply the same conditions (e.g. the Tracking card's
  // feature check) to the same values ...
  const headerUtilityProps = useMemo(() => ({
    Features,
    globalGroupTitle,
    isViewingGlobalGroup,
    tooltipClasses,
    trackingFilterBadgeCount,
    trackingFilterSelected,
    trackingSelected
  }), [
    Features,
    globalGroupTitle,
    isViewingGlobalGroup,
    tooltipClasses,
    trackingFilterBadgeCount,
    trackingFilterSelected,
    trackingSelected
  ]);

  const headerContent = useCallback((collapsed: boolean) => (
    <NavigationHeaderContent collapsed={collapsed}
      {...headerUtilityProps}
      onBrandClick={onBrandClick}
      onOpenGroupChooser={openGroupChooser}
      onOpenTracking={openTracking}
      onOpenTrackingFilter={openTrackingFilter}
      onOpenTrackingSearch={openTrackingSearch} />
  ), [
    headerUtilityProps,
    onBrandClick,
    openGroupChooser,
    openTracking,
    openTrackingFilter,
    openTrackingSearch
  ]);

  const mobileHeaderContent = useCallback((_: boolean, onClose?: () => void) => (
    <NavigationHeaderUtilities collapsed={false}
      mobile
      {...headerUtilityProps}
      onOpenGroupChooser={getMobileHeaderAction(openGroupChooser, onClose)}
      onOpenTracking={getMobileHeaderAction(openTracking, onClose)}
      onOpenTrackingFilter={getMobileHeaderAction(openTrackingFilter, onClose)}
      onOpenTrackingSearch={getMobileHeaderAction(openTrackingSearch, onClose)} />
  ), [
    headerUtilityProps,
    getMobileHeaderAction,
    openGroupChooser,
    openTracking,
    openTrackingFilter,
    openTrackingSearch
  ]);

  // the Environment Indicator is rendered independently of the Theme Toggle conditions,
  // as the external environment warning must remain visible for resellers, when multiple
  // themes are disabled, and while the navigation rail is collapsed ...
  const footerContent = useCallback((collapsed: boolean) => {
    // the New iOn switch shows collapsed as well - the switch alone - and sits
    // right after the footer items, as one of them - only for users with the
    // `dls` Feature Flag ...
    const showThemeToggle = multiThemeEnabled && !isReseller && hasNewThemeToggleAccess();
    return (
      <>
        <Render.If condition={showThemeToggle}>
          <NewIonThemeSwitch collapsed={collapsed} />
        </Render.If>
        <Render.If condition={isExternalEnvironment}>
          <div style={collapsed ? collapsedFooterItemStyle : footerItemStyle}>
            <EnvironmentIndicator collapsed={collapsed} />
          </div>
        </Render.If>
      </>
    );
  }, [isExternalEnvironment, isReseller, multiThemeEnabled]);

  return {
    closeContactSupport,
    closeSecondaryNavigation,
    currentThemeNavigationProps : {
      footerContent,
      footerItems,
      headerContent,
      mobileHeaderContent
    },
    isContactSupportOpen,
    markSkipNextRouteSync,
    openContactSupport
  };
};

export default useCurrentNavigation;
